import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type {
  AuthContextData,
  GlobalRole,
  Permission,
  Profile,
  SpaceMembership,
} from './permissions'

/**
 * Sitzung und Berechtigungskontext.
 *
 * Was hier im Browser liegt, ist eine KOPIE dessen, was die Datenbank ohnehin
 * durchsetzt. Sie dient dazu, Bedienelemente auszublenden, die der Server
 * ablehnen wuerde. Wer sie manipuliert, sieht hoechstens eine Schaltflaeche,
 * die dann eine 403 kassiert.
 */

export interface AuthState {
  /** null = nicht angemeldet, undefined = noch nicht geladen */
  session: Session | null | undefined
  user: User | null
  profile: Profile | null
  globalRole: GlobalRole | null
  permissions: ReadonlySet<Permission>
  spaces: SpaceMembership[]
  loading: boolean
  /** Kontext konnte nicht geladen werden (Netz, Konfiguration, RLS). */
  error: string | null

  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  requestPasswordReset: (email: string) => Promise<{ error: string | null }>
  updatePassword: (password: string) => Promise<{ error: string | null }>
  /** Kontext neu laden — nach Rollenaenderungen oder eingeloester Einladung. */
  reload: () => Promise<void>
}

export const AuthContext = createContext<AuthState | undefined>(undefined)

const EMPTY_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>()

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined)
  const [context, setContext] = useState<AuthContextData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Verhindert, dass eine ueberholte Antwort eine neuere ueberschreibt.
  const requestId = useRef(0)

  /*
    FUER WEN DER KONTEXT GERADE GILT.

    Der Grund, warum es diese Referenz gibt: `supabase-js` meldet bei JEDEM
    Zurueckkommen ins Fenster ein `SIGNED_IN` — nach dem Minimieren, nach einem
    Tabwechsel, nach dem Aufwachen des Rechners. Das ist kein neuer Anmeldevorgang,
    sondern eine erneute Bestaetigung derselben Sitzung.

    Behandelt man es wie eine Anmeldung, laeuft jedes Mal `setLoading(true)`, die
    Oberflaeche faellt auf den Ladeschirm zurueck, die App-Shell wird neu
    aufgebaut und die Buehne setzt neu auf. Von aussen sieht das aus, als lade
    die Seite neu — und genau so wurde es auch gemeldet.

    Verglichen wird deshalb die BENUTZERKENNUNG. Ist sie dieselbe, wird nur die
    Sitzung nachgezogen und sonst nichts angefasst.
  */
  const loadedFor = useRef<string | null>(null)

  const loadContext = useCallback(async (activeSession: Session | null) => {
    const id = ++requestId.current

    if (!activeSession) {
      if (id === requestId.current) {
        loadedFor.current = null
        setContext(null)
        setError(null)
        setLoading(false)
      }
      return
    }

    try {
      // Eine Abfrage fuer Profil, globale Rolle, Rechte und Bereiche.
      const { data, error: rpcError } = await supabase.rpc('my_context')
      if (id !== requestId.current) return

      if (rpcError) {
        setError(rpcError.message)
        setContext(null)
      } else {
        setContext(data as AuthContextData)
        setError(null)
        loadedFor.current = activeSession.user.id
      }
    } catch (err) {
      if (id !== requestId.current) return
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler')
      setContext(null)
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      void loadContext(data.session)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return
      setSession(nextSession)

      // TOKEN_REFRESHED aendert die Rechte nicht — der Kontext bleibt gueltig.
      // Ein erneuter RPC bei jedem Refresh waere reine Last.
      if (event === 'TOKEN_REFRESHED') return

      /*
        DIESELBE PERSON WIE VORHER: nichts neu laden.

        `SIGNED_IN` kommt auch beim blossen Zurueckkommen ins Fenster. Ohne
        diesen Vergleich fiel die Oberflaeche dann auf den Ladeschirm, und der
        ganze Aufbau — Shell, Buehne, WebGL-Kontext — begann von vorn. Wer nur
        kurz das Fenster gewechselt hatte, sah die Seite neu starten.
      */
      const nextUser = nextSession?.user.id ?? null
      if (nextUser !== null && nextUser === loadedFor.current) return

      setLoading(true)
      void loadContext(nextSession)
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [loadContext])

  const signIn = useCallback<AuthState['signIn']>(async (email, password) => {
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })
    return { error: signInError?.message ?? null }
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    setContext(null)
  }, [])

  const requestPasswordReset = useCallback<AuthState['requestPasswordReset']>(
    async (email) => {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        { redirectTo: `${window.location.origin}/auth/reset` },
      )
      return { error: resetError?.message ?? null }
    },
    [],
  )

  const updatePassword = useCallback<AuthState['updatePassword']>(async (password) => {
    const { error: updateError } = await supabase.auth.updateUser({ password })
    return { error: updateError?.message ?? null }
  }, [])

  const reload = useCallback(async () => {
    // Rollen stehen im Token. Nach einer Rechteaenderung muss es erneuert
    // werden, sonst arbeitet die App mit dem alten Rechtestand weiter.
    const { data } = await supabase.auth.refreshSession()
    const next = data.session ?? (await supabase.auth.getSession()).data.session
    setSession(next)
    await loadContext(next)
  }, [loadContext])

  const permissions = useMemo(
    () =>
      context?.permissions?.length
        ? (new Set(context.permissions) as ReadonlySet<Permission>)
        : EMPTY_PERMISSIONS,
    [context],
  )

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      profile: context?.profile ?? null,
      globalRole: context?.global_role ?? null,
      permissions,
      spaces: context?.spaces ?? [],
      loading,
      error,
      signIn,
      signOut,
      requestPasswordReset,
      updatePassword,
      reload,
    }),
    [
      session,
      context,
      permissions,
      loading,
      error,
      signIn,
      signOut,
      requestPasswordReset,
      updatePassword,
      reload,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
